// Mirrors 3d-backend/services/spatial-vision/runSettings.js. The backend validates
// again, so a value missing here is only a UI gap, never a wrong job.
export const TILES = [1, 2, 3];
// Detection passes: full frame, then a crop around each instance found so far.
// Capped at 2: passes 3 and 4 measured zero gain on 54 images at +23% / +37% time
// (scratchpad/gap-detect/video-study/route-bench2/REPORT.md).
export const PASSES = [1, 2];
export const DEFAULT_PASSES = 1;
// Detection score floor: findings the detector scores below it are not returned.
export const CONFIDENCE = [0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.5];
export const DEFAULT_CONFIDENCE = 0.3;
export const MAX_TOKENS = [512, 1024, 1536, 2048, 4096];
// 0 = keep: the detector sees the uploaded pixels, with no resize on the way in.
export const MAX_IMAGE_SIDE_KEEP = 0;
export const MAX_IMAGE_SIDES = [MAX_IMAGE_SIDE_KEEP, 768, 1024, 1536, 2048];
export const FPS_VALUES = [5, 3, 1, 0.5, 0.3, 0.1, 0.05, 0.01, 0.005];
export const VLM_VERSION_IDS = ['super-light', 'light', 'regular', 'pro'];
export const DEFAULT_FPS = 1;

const pickNumber = (value, allowed) => {
  if (value === undefined || value === null || value === '') return undefined;
  const numeric = Number(value);
  return allowed.includes(numeric) ? numeric : undefined;
};

// Unlike the list settings, an out-of-range pass count is clamped rather than
// dropped: the worker treats a missing key as DEFAULT_PASSES, and an operator who
// asked for "more" should not silently fall back to fewer.
const clampInteger = (value, allowed) => {
  if (value === undefined || value === null || value === '') return undefined;
  const numeric = Math.round(Number(value));
  if (!Number.isFinite(numeric)) return undefined;
  return Math.min(allowed[allowed.length - 1], Math.max(allowed[0], numeric));
};

const pickBoolean = value => {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'string') {
    return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
  }
  return Boolean(value);
};

export function normalizeRunSettings(input) {
  if (!input || typeof input !== 'object') return {};
  const settings = {
    tiles: pickNumber(input.tiles, TILES),
    passes: clampInteger(input.passes, PASSES),
    confidence: pickNumber(input.confidence, CONFIDENCE),
    maxTokens: pickNumber(input.maxTokens, MAX_TOKENS),
    maxImageSide: pickNumber(input.maxImageSide, MAX_IMAGE_SIDES),
    fps: pickNumber(input.fps, FPS_VALUES),
    vlmVersion: VLM_VERSION_IDS.includes(input.vlmVersion)
      ? input.vlmVersion
      : undefined,
    dedupe: pickBoolean(input.dedupe),
  };
  return Object.fromEntries(
    Object.entries(settings).filter(([, value]) => value !== undefined),
  );
}

// Half the thresholds are written with one decimal and half with two, and a column
// mixing 0.2 with 0.15 reads as two different kinds of number.
// The finding cards print each detection's own confidence as a percentage, so the
// threshold that decides which ones survive is written the same way. Two formats for
// one quantity make the operator do arithmetic to compare the setting with the result.
export const confidenceLabel = value => `${Math.round(value * 100)}%`;

// The empty option of a settings select means "whatever the service decides". Where
// the row has a default, the number it would use is already one of the options below
// it, so the empty one has to read as the choice it is and not as that number again.
export const defaultOptionLabel = shown =>
  shown === undefined ? 'Default' : `Default (${shown})`;

const RUNNING = ['pending', 'queued', 'running'];
const plural = (count, word) =>
  `${count} ${word}${count === 1 ? '' : word.endsWith('s') ? 'es' : 's'}`;
const SUMMARY = [
  ['tiles', value => plural(value, 'tile')],
  ['passes', value => plural(value, 'pass')],
  ['confidence', value => `${confidenceLabel(value)} minimum confidence`],
  ['maxTokens', value => `${value} tokens`],
  [
    'maxImageSide',
    value =>
      value === MAX_IMAGE_SIDE_KEEP ? 'original resolution' : `${value} px`,
  ],
  ['fps', value => `${value} FPS`],
];

// A job takes its settings when it is submitted, and the worker never reads them again.
// So the panel stays open for the next run while one is in progress, and says what the
// running one uses; otherwise an edit made mid-run reads as a change to that run.
export function runningSettingsNote(jobs) {
  const running = (jobs || []).filter(job => RUNNING.includes(job.status));
  if (!running.length) return null;
  const settings = normalizeRunSettings(running[0].metadata?.runSettings);
  const parts = SUMMARY.filter(([key]) => settings[key] !== undefined).map(
    ([key, text]) => text(settings[key]),
  );
  const uses = parts.length ? parts.join(', ') : 'the service defaults';
  const head =
    running.length === 1
      ? `A run is in progress with ${uses}`
      : `${running.length} runs are in progress; the newest uses ${uses}`;
  return `${head}. Changes here apply to the next run.`;
}

export function versionsForTier(catalog, serviceTier) {
  return (catalog || []).filter(version => version.tier === serviceTier);
}

// A version stored for this user that the tier cannot run (not offered on it, or offered as
// "not available") made every run fail with 409 until the operator found and changed the select;
// without it the service picks its default.
export function runnableSettings(settings, catalog, serviceTier) {
  const runnable = versionsForTier(catalog, serviceTier).some(
    version => version.id === settings?.vlmVersion && version.available,
  );
  if (!settings?.vlmVersion || runnable) return { ...settings };
  const { vlmVersion, ...rest } = settings;
  return rest;
}
